/**
 * @file settings/index.tsx
 * @description use this file to export site wide settings
 *
 * @author ogarcia (ozkary)
 *
 */

/**
 * Imports.
 */
import * as _cache from '../cache';
import * as RatesModels from '../../functions/rates/rates.model';
import { CachedQuotes } from '../../services/quotes/model';
import * as _ratesFunc from '../../functions/rates';

let settings: RatesModels.Settings | undefined;

const setSettings = async (
  retries = 1
): Promise<RatesModels.Settings | undefined> => {
  if (!retries) {
    settings = undefined;
    return Promise.resolve(settings);
  }
  if (settings) {
    return Promise.resolve(settings);
  }

  await new Promise<void>((resolve) => {
    const timer = setTimeout(() => {
      clearTimeout(timer);
      resolve();
    }, 500);
  });
  return await setSettings(retries - 1);
};

// app edition configuration
const edition = process.env.REACT_APP_EDITION;
const renew = process.env.REACT_APP_EDITION_RENEW;
const nullCoverage = process.env.REACT_APP_ALLOW_NULL_COVERAGE;
const banner = process.env.REACT_APP_SHOW_BANNER;

/**
 * app edition is set to vsc
 */
export const isVsc = edition === 'vsc';

/**
 * app renewal feaure is active
 */
export const isRenewal = renew === 'true';

/**
 * app allow null coverage on currenct contract for renew edition is acctive
 */
export const allowNullCoverage = nullCoverage === 'true';

/**
 * true to display the toolbar on the plan selection component
 * TODO move to JSON if becomes edition agnostic
 */
export const showCoverageButtons: boolean = isVsc;

/**
 * shows the converage expires message
 */
export const showCoverageExpires: boolean = isVsc && !isRenewal;

/**
 * Indicates whether to display the banner component or not.
 */
export const showBanner = banner === 'true';

/**
 * returns the digital signature text from the settings
 * add signature settings on the rate json file
 */
export const digitalSignature: string = settings?.signature || '';

export const getSettings = (): RatesModels.Settings | undefined => {
  const document = _cache.getItem<RatesModels.Document>(_cache.Key.rates);
  if (document && document.settings) {
    return document.settings;
  }

  return undefined;
};

/**
 * Gets services for the program.
 * @returns Program services.
 */
export const getServices = (): string[] | null => {
  const quotes = _cache.getItem<CachedQuotes>(
    _cache.Key.quotesCache
  );
  const document = _cache.getItem<RatesModels.Document>(_cache.Key.rates);
  if (document && document.programs) {
    const programs = document.programs;
    const key = quotes?.response?.programs?.Value ?? '';
    return programs?.[key]?.services ?? null;
  }

  return null;
};

/**
 * Gets phone number from settings.
 * @returns Phone number.
 */
export const getPhoneNumber = async (): Promise<string | undefined> => {
  if (!settings) {
    await setSettings();
  }

  return settings?.phone;
};

/**
 * Gets vehicle options.
 * @returns Vehicle options.
 */
export const getShowVehicleOptions = async (): Promise<boolean | undefined> => {
  return (settings || (await setSettings()))?.showVehicleOptions;
};

/**
 * Gets brand setup on configs.
 * @returns Brand setting.
 */
export const getBrand = (): string => {
  const brandName = process.env.REACT_APP_BRAND_LABEL ?? '';

  if (brandName) {
    return brandName;
  } else {
    const document = _cache.getItem<RatesModels.Document>(_cache.Key.rates);
    if (document) return document.brand ?? '';
  }

  return '';
};

/**
 * Gets settings from static file
 * @returns Settings
 */
const getStaticSettings = async (): Promise<RatesModels.Settings> => {
  const resources = (async (): Promise<RatesModels.Settings> =>
    await import('../../content/settings.json'))();
  return resources;
};

/**
 * Gets dicounts on configs.
 * @returns Discounts.
 */
export const getDiscounts = async (): Promise<
  RatesModels.Discount[] | undefined
> => {
  const currentDiscount = (settings || (await getStaticSettings()))?.discounts;

  return currentDiscount;
};

/**
 * Gets dicounts exclude states.
 * @returns Discounts.
 */
export const getDiscountExcludeStates = async (): Promise<
  string[] | undefined
> => {
  const currentDiscountExcludeStates = (settings || (await setSettings()))
    ?.discountExcludeStates;

  if (currentDiscountExcludeStates) {
    return currentDiscountExcludeStates;
  }

  return (await getStaticSettings())?.discountExcludeStates;
};

export const loadSettings = async (): Promise<void> => {
  const currentSettings = getSettings();
  let document;
  if (!currentSettings) {
    document = await _ratesFunc.getRates(process.env.REACT_APP_EDITION);
    _cache.setItem<RatesModels.Document>(_cache.Key.rates, document);
  }

  if (!settings) {
    settings = document?.settings ?? currentSettings;
  }
};

/**
 * Gets flag to hide make and model.
 * @returns Flag flag to hide make and model.
 */
export const hideMakeAndModel = (): boolean => {
  if (process.env.REACT_APP_HIDE_MAKEMODEL === 'true') {
    return true;
  }

  return false;
};

/**
 * Defines whether vin details api is enabled or not.
 */
export const isVinApiEnabled = process.env.REACT_APP_API_VEHICLE === 'true';

/**
 * set REACT_APP_SHOW_PROCESS_ICONS = true to display the process icons
 */
export const showProcessIcons = process.env.REACT_APP_SHOW_PROCESS_ICONS === 'true';
